'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useMemo, useState } from 'react';
import { InputV2, TreeAccordion } from 'ui-components';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { FlatFacetShell } from '../FlatFacetShell';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { PublisherFacetProps } from './PublisherFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

// The controller fetches a much larger value set (numberOfValues: 1000) so the
// search box can match the full publisher list client-side, but rendering it
// all at once is wasteful — while the search box is empty, only this many rows
// are mounted (plus any selected values beyond the cutoff). There is no
// optimistic/current merge: the generated hook's values ARE the current
// response, already in Coveo's occurrences order.
const IDLE_RENDER_LIMIT = 20;

export const PublisherFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PublisherFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const publisherLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-publisher-label',
    'Publisher'
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.clear',
    'Clear'
  );

  const searchPlaceholder = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-publisher-placeholder',
    'Search publishers...'
  );

  const [searchQuery, setSearchQuery] = useState('');

  const controllers = useSearchControllers();
  const publisherFacet = controllers.usePublisherFacet();
  const {
    values,
    selectedIds,
    selectedCount,
    isReady,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({ facet: publisherFacet, getValueId });

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      const top = values.slice(0, IDLE_RENDER_LIMIT);
      const inTop = new Set(top.map((v) => v.value));
      const selectedBeyondTop = values.filter(
        (v) => v.state === 'selected' && !inTop.has(v.value)
      );
      return [...top, ...selectedBeyondTop];
    }
    return values.filter((v) => v.value.toLowerCase().includes(query));
  }, [values, searchQuery]);

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      filteredValues.map((v) => ({
        id: getValueId(v),
        label: v.value,
        count: v.numberOfResults,
        disabled:
          !isReady || (v.numberOfResults === 0 && v.state !== 'selected'),
      })),
    [filteredValues, isReady]
  );

  const content = (
    <div className="flex flex-col">
      <InputV2
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-1 w-full text-sm"
        aria-label={searchPlaceholder}
      />
      <div className="max-h-[200px] overflow-y-auto">
        <TreeAccordion
          idPrefix={getFacetTreeIdPrefix(variant)}
          items={treeItems}
          selectionMode="multiple"
          selectedIds={selectedIds}
          onSelectMany={handleSelectMany}
          isDarkTheme={isDark}
          size={variant === 'sidebar' ? 'default' : 'sm'}
          reserveExpandSpace={variant !== 'horizontal'}
        />
      </div>
    </div>
  );

  return (
    <FlatFacetShell
      title={publisherLabel}
      clearLabel={clearLabel}
      selectedCount={selectedCount}
      onClear={handleClear}
      variant={variant}
      className={className}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!isReady}
    >
      {content}
    </FlatFacetShell>
  );
};
